<script>
import Contact from '@phoenix/mixins/components/light-checkout/contact/Contact';
import CheckboxListField from '@maison/components/form/fields/CheckboxListField.vue';
import TextareaField from '@maison/components/form/fields/TextareaField.vue';
import SelectField from '@maison/components/form/fields/SelectField.vue';
import formAddressFieldsMixin from '@phoenix/mixins/formAddressFieldsMixin';
import { defineAsyncComponent } from 'vue';
import formBasicMixin from '@phoenix/mixins/formBasicMixin';
import { set } from 'lodash-es';
import CartItem from '@maison/components/light-checkout/order/CartItem.vue';
import LightCheckoutSection from '@maison/components/light-checkout/LightCheckoutSection.vue';

export default {
  components: {
    CartItem,
    PhoenixForm: defineAsyncComponent(() => import('@maison/components/form/PhoenixForm.vue')),
    SelectField,
    TextareaField,
    CheckboxListField,
    LightCheckoutSection,
  },

  mixins: [formBasicMixin, formAddressFieldsMixin, Contact],

  props: {
    reference: {
      type: Object,
      required: true,
    },
    blockId: {
      type: Number,
      required: true,
    },
  },

  data() {
    return {
      formConfigName: 'contact',
    };
  },

  computed: {
    formAction() {
      return window.route('api.lightecom.contact.checkout');
    },
    formRedirectUrl() {
      return window.route('light_ecom_confirmation_page');
    },
  },

  watch: {
    'fields.phone'() {
      set(this.fields, ['phone', 'required'], this.fieldIsPresent('phone'));
    },
  },

  created() {
    this.setFields({
      country: {
        id: 'country',
        name: 'country',
        label: this.$t('Form.label.country'),
        value: null,
        options: [],
        required: true,
        type: 'select',
        forceSelect: true,
      },
      preferredContact: {
        id: 'preferredContact',
        name: 'preferredContact',
        label: this.$t('Form.label.preferedContactMode'),
        value: [],
        required: true,
        options: [
          {
            id: 'contact-mode-email',
            value: 'email',
            label: this.$t('LightCheckout.contactMode.email'),
          },
          {
            id: 'contact-mode-phone',
            value: 'phone',
            label: this.$t('LightCheckout.contactMode.phone'),
          },
          {
            id: 'contact-mode-messaging',
            value: 'messaging',
            label: this.$t('LightCheckout.contactMode.messaging'),
          },
        ],
      },
      additionalInfo: {
        id: 'additionalInfo',
        name: 'additionalInfo',
        label: this.$t('LightCheckout.additionalInformationsTitle'),
        value: '',
        required: false,
      },
      reference: {
        id: 'reference',
        value: this.reference?.modelReference,
        type: 'hidden',
      },
      block: {
        id: 'blockId',
        value: this.blockId,
        type: 'hidden',
      },
      newsletter: {
        id: 'newsletter',
        name: 'newsletter',
        label: this.$t('Form.stayInTouch', {
          privacyLink: this.privacyLink,
        }),
        type: 'checkbox',
        value: false,
      },
    });
  },

  methods: {
    pushFormSubmittedEvent() {
      window.tracking.pushEvent({
        event: 'order_online_form_submitted',
        reference: this.reference.modelReference,
        country: this.fields.country.value,
        website_version: window.Phoenix.config.RELEASE,
      });
    },
    onFormSubmitSuccess() {
      this.pushFormSubmittedEvent();
    },
  },
};
</script>

<template>
  <div class="container max-md:container--tight lg:container--lg" data-cy="light-checkout-contact-form">
    <section class="checkout section pt-0">
      <div class="container container--md">
        <form class="space-y-8">
          <light-checkout-section
            name="cart"
            :title="$t('LightCheckout.cartTitle')"
            :subtitle="$t('LightCheckout.cartSubtitle')"
          >
            <div class="pb-6 pt-6">
              <ul class="product-card-list">
                <li class="product-card-list__entry">
                  <cart-item v-if="reference" :item="reference" data-cy="checkout-cart-item" />
                </li>
              </ul>
            </div>

            <phoenix-form
              ref="phoenixForm"
              v-model="fields"
              :action="formAction"
              :on-success-callback="onFormSubmitSuccess"
              form-type="contact"
              :fill-customer-info="false"
              korean-form-type="contact"
              :custom-submit-text="$t('LightCheckout.submitOrder')"
              :redirect-url="formRedirectUrl"
              :captcha-enabled="true"
              :terms-link="termsLink"
            >
              <template #default="{ shouldDisplayNewsletter }">
                <div class="flex-grid flex-col md:flex-row flex-grid--x--md flex-grid--y--md">
                  <h3 class="h5 mb-4">{{ $t('LightCheckout.contactStep') }}</h3>
                  <div class="flex-grid flex-grid--y--md content-start w-full">
                    <div class="w-full">
                      <form-group v-if="fieldIsPresent('title')" :attrs="fields.title">
                        <select-field v-model="fields.title.value" />
                      </form-group>
                      <form-group v-if="fieldIsPresent('gender')" :attrs="fields.gender">
                        <select-field v-model="fields.gender.value" />
                      </form-group>
                    </div>
                    <div v-if="fieldIsPresent('firstName2') && isArabicLanguage" class="w-full">
                      <form-group :attrs="fields.firstName2">
                        <text-field v-model="fields.firstName2.value" />
                      </form-group>
                    </div>
                    <div v-if="fieldIsPresent('lastName2') && isArabicLanguage" class="w-full">
                      <form-group :attrs="fields.lastName2">
                        <text-field v-model="fields.lastName2.value" />
                      </form-group>
                    </div>
                    <div v-if="fieldIsPresent('firstName1')" class="w-full">
                      <form-group :attrs="fields.firstName1">
                        <text-field v-model="fields.firstName1.value" />
                      </form-group>
                    </div>
                    <div v-if="fieldIsPresent('lastName1')" class="w-full">
                      <form-group :attrs="fields.lastName1">
                        <text-field v-model="fields.lastName1.value" />
                      </form-group>
                    </div>
                    <div v-if="fieldIsPresent('firstName2') && !isArabicLanguage" class="w-full">
                      <form-group :attrs="fields.firstName2">
                        <text-field v-model="fields.firstName2.value" />
                      </form-group>
                    </div>
                    <div v-if="fieldIsPresent('lastName2') && !isArabicLanguage" class="w-full">
                      <form-group :attrs="fields.lastName2">
                        <text-field v-model="fields.lastName2.value" />
                      </form-group>
                    </div>
                    <div v-if="fieldIsPresent('email')" class="w-full">
                      <form-group :attrs="fields.email">
                        <text-field v-model="fields.email.value" />
                      </form-group>
                    </div>
                    <div v-if="fieldIsPresent('phone')" class="w-full">
                      <form-group :attrs="fields.phone">
                        <!-- We need to wrap the `PhoneField` in a div as there is a strange behaviour when the component is destroyed -->
                        <div>
                          <phone-field v-model="fields.phone.value" />
                        </div>
                      </form-group>
                    </div>
                    <div class="w-full">
                      <form-group :attrs="fields.preferredContact">
                        <checkbox-list-field v-model="fields.preferredContact.value" inline />
                      </form-group>
                    </div>
                    <div class="w-full">
                      <form-group :attrs="fields.country">
                        <select-field v-model="fields.country.value" />
                      </form-group>
                    </div>
                    <div class="w-full">
                      <form-group :attrs="fields.additionalInfo">
                        <textarea-field v-model="fields.additionalInfo.value" />
                      </form-group>
                    </div>
                    <div v-if="shouldDisplayNewsletter" class="w-full">
                      <form-group :show-label="false" :attrs="fields.newsletter">
                        <checkbox-field v-model="fields.newsletter.value" />
                      </form-group>
                    </div>
                  </div>
                </div>
              </template>
            </phoenix-form>
          </light-checkout-section>
        </form>
      </div>
    </section>
  </div>
</template>
